/**
 * useAdminDashboardOverview — single fetch for /admin home KPIs + live pulse.
 * Auto-refreshes every 30s (throttled). Abort on unmount.
 */

import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useCallback, useEffect, useRef, useState } from "react";

export type DashboardOverview = {
  generated_at: string;
  window: {
    revenue_days: number;
    live_seconds: number;
    leads_hours: number;
    photos_days: number;
  };
  money: {
    revenue_7d_cents: number;
    tx_7d: number;
    unique_customers: number;
    currency: string;
  };
  live: {
    active_sessions: number;
    events_last_5m: number;
    top_events: Array<{ event_name: string; count: number }>;
    latest: Array<{
      event_name: string;
      country: string | null;
      city: string | null;
      created_at: string;
      customer_email: string | null;
    }>;
  };
  leads: {
    emails_captured_24h: number;
    with_coupon_promo50_24h: number;
    recoverable_2_24h: number;
    completed_24h: number;
  };
  photos: {
    sessions_24h: number;
    views_24h: number;
    total_dwell_ms_24h: number;
    top_countries: Array<{ country: string; views: number }>;
  };
  memberships: {
    pending_approval: number;
    brand_active_7d?: number;
  };
  buyers?: {
    memberships_7d?: number;
    stripe_paid_2d?: number;
    stripe_paid_2d_cents?: number;
    stripe_ok?: boolean;
    stripe_error?: string | null;
    latest?: Array<{
      email: string;
      amount: number | null;
      currency: string | null;
      country: string | null;
      flow_name: string | null;
      started_at: string | null;
      data_source: string;
    }>;
  };
  offer_engine?: {
    note?: string;
  };
};

const REFRESH_MS = 30_000;
const MIN_REFETCH_GAP_MS = 15_000;

export function useAdminDashboardOverview(enabled: boolean) {
  const { getAuthHeaders } = useAdminAuth();
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const lastFetchRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const hasDataRef = useRef(false);

  const fetchOverview = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!enabled) return;
      const now = Date.now();
      if (
        opts?.silent &&
        hasDataRef.current &&
        now - lastFetchRef.current < MIN_REFETCH_GAP_MS
      ) {
        return;
      }
      lastFetchRef.current = now;

      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      if (!opts?.silent) setLoading(true);
      try {
        const headers = await getAuthHeaders();
        const res = await fetch("/api/admin/dashboard-overview", {
          headers,
          signal: ac.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as DashboardOverview;
        if (ac.signal.aborted) return;
        hasDataRef.current = true;
        setData(json);
        setUpdatedAt(Date.now());
        setError(null);
      } catch (e) {
        if (ac.signal.aborted) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    },
    [enabled, getAuthHeaders],
  );

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    void fetchOverview();
    const id = window.setInterval(() => {
      void fetchOverview({ silent: true });
    }, REFRESH_MS);
    return () => {
      window.clearInterval(id);
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount + enabled only
  }, [enabled]);

  return {
    data,
    loading,
    error,
    updatedAt,
    refresh: () => fetchOverview({ silent: false }),
  };
}
